'use client';

import * as React from 'react';
import { cn } from '../../lib/utils';
import { BackHeaderButton } from '../patterns/back-link';
import { SidebarInset, SidebarProvider } from '../ui/sidebar';
import { ScrollPane } from '../patterns/scroll-pane';
import { useResponsiveSidebarOpen } from '../../hooks/use-responsive-sidebar-open';
import { SHELL_HEADER_BAR_CLASSNAME, SHELL_HEADER_BAR_EXTENDED_CLASSNAME } from './shell-chrome';
import { ConsoleSidebar, type ConsoleSidebarProps } from './console-sidebar';

export type ConsoleHeaderBackNavigation = {
  href: string;
  label: string;
};

type ConsoleHeaderChromeContextValue = {
  setHeaderActions: (actions: React.ReactNode) => void;
  setHeaderTitle: (title: string | undefined) => void;
  setHeaderTitleAddon: (addon: React.ReactNode) => void;
  setHeaderDescription: (description: string | undefined) => void;
  setFooter: (footer: React.ReactNode) => void;
};

const ConsoleHeaderChromeContext = React.createContext<ConsoleHeaderChromeContextValue | null>(
  null,
);

/** Register page-level actions in the console shell header (top right). */
export function useConsoleHeaderActions(actions: React.ReactNode | null) {
  const context = React.useContext(ConsoleHeaderChromeContext);

  React.useEffect(() => {
    if (!context) return;
    context.setHeaderActions(actions);
    return () => context.setHeaderActions(null);
  }, [context, actions]);
}

/** Override the shell header title for the current page (e.g. resumable create wizards). */
export function useConsoleHeaderTitle(title: string | undefined) {
  const context = React.useContext(ConsoleHeaderChromeContext);

  React.useEffect(() => {
    if (!context) return;
    context.setHeaderTitle(title);
    return () => context.setHeaderTitle(undefined);
  }, [context, title]);
}

/** Render content beside the shell header title (e.g. status badges). */
export function useConsoleHeaderTitleAddon(addon: React.ReactNode | null) {
  const context = React.useContext(ConsoleHeaderChromeContext);

  React.useEffect(() => {
    if (!context) return;
    context.setHeaderTitleAddon(addon);
    return () => context.setHeaderTitleAddon(null);
  }, [context, addon]);
}

/** Override the shell header description for the current page. */
export function useConsoleHeaderDescription(description: string | undefined) {
  const context = React.useContext(ConsoleHeaderChromeContext);

  React.useEffect(() => {
    if (!context) return;
    context.setHeaderDescription(description);
    return () => context.setHeaderDescription(undefined);
  }, [context, description]);
}

/** Register a full-width sticky footer below the scroll region (create wizards). */
export function useConsoleFooter(footer: React.ReactNode | null) {
  const context = React.useContext(ConsoleHeaderChromeContext);

  React.useEffect(() => {
    if (!context) return;
    context.setFooter(footer);
    return () => context.setFooter(null);
  }, [context, footer]);
}

export type ConsoleDashboardShellProps = {
  sidebar: ConsoleSidebarProps;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  /** Optional title shown in the content header. */
  headerTitle?: string;
  /** Optional subtitle under the title (same foreground color, smaller type). */
  headerDescription?: string;
  /** Optional back control — social-style icon button beside the title. */
  headerBack?: ConsoleHeaderBackNavigation | null;
  /** Optional static actions rendered in the header (prefer `useConsoleHeaderActions` for dynamic pages). */
  headerActions?: React.ReactNode;
};

/**
 * Dashboard console shell — shadcn sidebar-07 layout with LOAMIFY nav + tokens.
 * Sidebar auto-expands on large screens and collapses to icons on smaller viewports.
 */
export function ConsoleDashboardShell({
  sidebar,
  children,
  className,
  contentClassName,
  headerTitle,
  headerDescription,
  headerBack = null,
  headerActions,
}: ConsoleDashboardShellProps) {
  const sidebarOpen = useResponsiveSidebarOpen();
  const [dynamicHeaderActions, setDynamicHeaderActions] = React.useState<React.ReactNode>(null);
  const [dynamicHeaderTitle, setDynamicHeaderTitle] = React.useState<string | undefined>();
  const [dynamicHeaderTitleAddon, setDynamicHeaderTitleAddon] = React.useState<React.ReactNode>(null);
  const [dynamicHeaderDescription, setDynamicHeaderDescription] = React.useState<string | undefined>();
  const [dynamicFooter, setDynamicFooter] = React.useState<React.ReactNode>(null);
  const resolvedHeaderActions = dynamicHeaderActions ?? headerActions ?? null;
  const resolvedHeaderTitle = dynamicHeaderTitle ?? headerTitle;
  const resolvedHeaderTitleAddon = dynamicHeaderTitleAddon;
  const resolvedHeaderDescription = dynamicHeaderDescription ?? headerDescription;

  const headerChromeContext = React.useMemo<ConsoleHeaderChromeContextValue>(
    () => ({
      setHeaderActions: setDynamicHeaderActions,
      setHeaderTitle: setDynamicHeaderTitle,
      setHeaderTitleAddon: setDynamicHeaderTitleAddon,
      setHeaderDescription: setDynamicHeaderDescription,
      setFooter: setDynamicFooter,
    }),
    [],
  );
  const mainScrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <SidebarProvider open={sidebarOpen} mobilePresentation="inline">
      <ConsoleHeaderChromeContext.Provider value={headerChromeContext}>
        <div className={cn('flex h-svh max-h-svh w-full overflow-hidden bg-background', className)}>
          <ConsoleSidebar {...sidebar} />
          <SidebarInset className="flex min-h-0 flex-col overflow-hidden">
            {resolvedHeaderTitle ? (
              <header
                className={cn(
                  resolvedHeaderDescription
                    ? SHELL_HEADER_BAR_EXTENDED_CLASSNAME
                    : SHELL_HEADER_BAR_CLASSNAME,
                  'justify-between border-b border-shell-border',
                )}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {headerBack ? (
                    <BackHeaderButton href={headerBack.href} label={headerBack.label} />
                  ) : null}
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <h1 className="truncate text-2xl font-extrabold leading-tight tracking-tight text-foreground">
                        {resolvedHeaderTitle}
                      </h1>
                      {resolvedHeaderTitleAddon ? (
                        <div className="flex shrink-0 items-center">{resolvedHeaderTitleAddon}</div>
                      ) : null}
                    </div>
                    {resolvedHeaderDescription ? (
                      <p className="mt-1 text-body-sm leading-snug text-foreground">
                        {resolvedHeaderDescription}
                      </p>
                    ) : null}
                  </div>
                </div>
                {resolvedHeaderActions ? (
                  <div className="flex shrink-0 items-center gap-2">{resolvedHeaderActions}</div>
                ) : null}
              </header>
            ) : null}
            <ScrollPane
              ref={mainScrollRef}
              className={cn('px-4 py-5 md:px-6 md:py-6', contentClassName)}
              containerClassName="min-h-0 flex-1"
              floatingControl={!dynamicFooter}
            >
              {children}
            </ScrollPane>
            {dynamicFooter ? (
              <footer className="shrink-0 border-t border-shell-border bg-background">
                {dynamicFooter}
              </footer>
            ) : null}
          </SidebarInset>
        </div>
      </ConsoleHeaderChromeContext.Provider>
    </SidebarProvider>
  );
}
